import React, { useCallback, useEffect, useRef, useState } from "react"
import { api, ApiError } from "../../lib/api"
import { GARANTIA, PARCELAS, VALIDADE_LINHA } from "../../lib/narrativa"
import { Button } from "../ui/Button"
import { Modal } from "../ui/Modal"
import { Spinner } from "../ui/Spinner"
import type { Plan, PixCharge, PurchaseStatus } from "../../types"

interface PagamentoModalProps {
  plan: Plan
  onClose: () => void
  /** Chamado quando o webhook confirma o pagamento (Pix) */
  onPaid: (purchaseId: string) => void
  /** Cartão (checkout Mercado Pago): cria a compra e redireciona */
  onCard?: () => void
  /**
   * Retomada de checkout: quando presente, opera sobre esta compra pendente já
   * existente (via /api/purchases/:id/{pix,checkout}) em vez de criar uma nova.
   */
  recoveryPurchaseId?: string | null
  /** Dias até a prova da pessoa (medição: quem compra perto da prova compra diferente). */
  diasParaProva?: number | null
}

type Step = "escolha" | "pix"

/**
 * Pagamento em dois passos. O passo 1 não tem campo nenhum: a pessoa vê o que
 * está liberando, a garantia e dois botões de peso igual (cartão em até 3x,
 * Pix à vista). Só quem escolhe Pix vê o CPF, e só depois de ver valor e
 * prazo — o único abandono medido até hoje foi no campo de CPF, aberto como
 * primeira coisa do modal antigo. O QR não pode vir antes do CPF (o Asaas
 * exige o customer com cpfCnpj), mas nada é pedido antes de a pessoa saber o
 * que está comprando.
 */
export function PagamentoModal({ plan, onClose, onPaid, onCard, recoveryPurchaseId, diasParaProva }: PagamentoModalProps) {
  const [step, setStep] = useState<Step>("escolha")
  const [cpf, setCpf] = useState("")
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [purchaseId, setPurchaseId] = useState<string | null>(null)
  const [pix, setPix] = useState<PixCharge | null>(null)
  const [copied, setCopied] = useState(false)
  const cpfTracked = useRef(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = null
  }, [])

  useEffect(() => stopPolling, [stopPolling])

  // Medir nunca pode segurar o fluxo de pagamento: dispara e não espera.
  function track(name: string, props: Record<string, unknown>) {
    api.post("/api/events", { name, plan: plan.slug, ...props }).catch(() => {})
  }

  useEffect(() => {
    if (!purchaseId || !pix) return
    pollRef.current = setInterval(() => {
      api
        .get<PurchaseStatus>(`/api/purchases/${purchaseId}`)
        .then((p) => {
          if (p.status === "paid") {
            stopPolling()
            onPaid(String(purchaseId))
          }
        })
        .catch(() => {
          /* rede oscilou: o próximo tick tenta de novo */
        })
    }, 3000)
    return stopPolling
  }, [purchaseId, pix, onPaid, stopPolling])

  function choosePix() {
    track("clique_pagar", { gateway: "pix", parcelas: 1, dias_para_prova: diasParaProva ?? undefined })
    setError(null)
    setStep("pix")
  }

  async function chooseCard() {
    track("clique_pagar", { gateway: "mercado_pago", parcelas: PARCELAS, dias_para_prova: diasParaProva ?? undefined })
    if (!recoveryPurchaseId) {
      onCard?.()
      return
    }
    setCreating(true)
    setError(null)
    try {
      const res = await api.post<{ init_point: string }>(`/api/purchases/${recoveryPurchaseId}/checkout`, {})
      window.location.href = res.init_point
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Não foi possível abrir o cartão. Tente de novo.")
      setCreating(false)
    }
  }

  function onCpfChange(value: string) {
    setCpf(value)
    // Primeiro dígito: separa "não quis dar o CPF" de "o Pix travou".
    if (!cpfTracked.current && value.replace(/\D/g, "").length > 0) {
      cpfTracked.current = true
      track("cpf_iniciado", {})
    }
  }

  async function generatePix() {
    setCreating(true)
    setError(null)
    try {
      // Retomada: gera o Pix da compra pendente existente; senão, cria uma nova.
      const res = recoveryPurchaseId
        ? await api.post<{ purchase_id: string; pix?: PixCharge; init_point?: string }>(
            `/api/purchases/${recoveryPurchaseId}/pix`,
            { cpf },
          )
        : await api.post<{ purchase_id: string; pix?: PixCharge; init_point?: string }>("/api/purchases", {
            plan_id: plan.id,
            method: "pix",
            cpf,
          })
      if (!res.pix && res.init_point) {
        // Asaas não configurado (dev): cai no fluxo antigo
        window.location.href = res.init_point
        return
      }
      setPurchaseId(String(res.purchase_id))
      setPix(res.pix ?? null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Não foi possível gerar o Pix. Tente de novo.")
    } finally {
      setCreating(false)
    }
  }

  async function copyPayload() {
    if (!pix) return
    try {
      await navigator.clipboard.writeText(pix.qr_payload)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      /* clipboard bloqueado: usuário pode selecionar manualmente */
    }
  }

  const cpfDigits = cpf.replace(/\D/g, "")
  const preco = plan.price_formatted
  const parcela = plan.installment_formatted

  return (
    <Modal open onClose={onClose} title={`Começar meu tratamento · ${preco}, uma vez`}>
      <div>
        {step === "escolha" && (
          <>
            <ul className="flex flex-col gap-1.5 text-sm text-navy-800">
              <li className="flex items-start gap-2">
                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" aria-hidden="true" />
                <span>
                  <span className="font-semibold">O que você está liberando:</span> etapas 3, 4 e 5 — Fortalecimento, Oficial e Linha de Corte, em qualquer prova da plataforma.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" aria-hidden="true" />
                <span>{VALIDADE_LINHA}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" aria-hidden="true" />
                <span>{GARANTIA}</span>
              </li>
            </ul>

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Button block variant="gold" onClick={() => void chooseCard()} disabled={creating}>
                {creating ? <Spinner className="h-4 w-4" /> : `Cartão — até ${PARCELAS}x de ${parcela}`}
              </Button>
              <Button block variant="secondary" onClick={choosePix} disabled={creating}>
                Pix — {preco} à vista
              </Button>
            </div>
            <p className="mt-3 text-center text-xs text-navy-400">
              Pagamento seguro — cartão pelo Mercado Pago, Pix pelo Asaas. Nada renova sozinho.
            </p>
          </>
        )}

        {step === "pix" && !pix && (
          <>
            <p className="text-sm font-semibold text-navy-800">
              {preco} no Pix · o código vale 3 dias
            </p>
            <label htmlFor="pix-cpf" className="mt-4 block text-sm font-medium text-navy-700">
              Seu CPF
            </label>
            <input
              id="pix-cpf"
              inputMode="numeric"
              autoComplete="off"
              placeholder="000.000.000-00"
              value={cpf}
              onChange={(e) => onCpfChange(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-3 text-base tabular-nums focus:border-navy-600 focus:outline-none"
            />
            <p className="mt-1.5 text-xs text-navy-400">
              O CPF vai só para o banco emitir o Pix. Não guardamos o seu CPF.
            </p>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            <Button block className="mt-4" onClick={() => void generatePix()} disabled={creating || cpfDigits.length !== 11}>
              {creating ? <Spinner className="h-4 w-4 text-white" /> : "Gerar QR Code Pix"}
            </Button>
            <button
              type="button"
              onClick={() => setStep("escolha")}
              disabled={creating}
              className="mt-3 w-full text-center text-sm font-medium text-navy-500 underline-offset-2 hover:underline disabled:opacity-50"
            >
              ← Voltar e pagar com cartão
            </button>
          </>
        )}

        {pix && (
          <>
            <div className="mt-2 flex flex-col items-center">
              <img
                src={`data:image/png;base64,${pix.qr_image_base64}`}
                alt="QR Code Pix"
                className="h-52 w-52 rounded-xl border border-slate-200"
              />
              <p className="mt-3 flex items-center gap-2 text-sm text-navy-600">
                <Spinner className="h-4 w-4 text-navy-400" />
                Aguardando pagamento — confirma sozinho aqui
              </p>
            </div>
            <Button block variant="secondary" className="mt-4" onClick={() => void copyPayload()}>
              {copied ? "Código copiado ✓" : "Copiar código Pix (copia e cola)"}
            </Button>
            <p className="mt-3 text-center text-xs text-navy-400">
              Abra o app do seu banco, escaneie o QR ou cole o código. A etapa 3 libera na hora do pagamento.
            </p>
          </>
        )}
      </div>
    </Modal>
  )
}
